import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import memoize from 'lodash/memoize';
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  getHumanReadableNameFromPaymentMethod,
  getIdentifier,
  isPaidByCreditCard,
} from '@atlassian/cssxp-utils-invoice';
import {
  FetchInvoicesForInvoiceGroupPageQuery,
  Invoice,
  InvoicePaymentMethod,
  FetchInvoicesForInvoiceGroupPageQueryVariables,
  Refunds,
  SortDirection,
} from '@css-xp/graphtypes';
//@ts-ignore
import InvoiceIcon from '@css-xp/base/assets/img/icon-invoice.svg';
import {
  FormattedCurrency,
  FormattedDate,
  OpenSidebarRow,
  ColGroup,
  SearchResultColumn,
  SearchResultsTable,
  TableBody,
  TableHead,
  SimpleTD,
  getTextAndTitle,
  useSidebar,
} from '@css-xp/base';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { fetchInvoicesForInvoiceGroupPage } from './queries/fetch-invoices-for-invoice-group';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import RefundChildProductRow from '../../invoices/src/components/invoice-child-refund-row';
import isNil from 'lodash/isNil';
// import {
//     Enabled,
//     FeatureSwitch,
// } from '../../common/security/containers/feature-switch';
// import { ENABLE_CREDIT_NOTES } from '../../../../../common/feature-flag-keys';

export const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;

interface Props {
  id: string;
  label?: string;
}

export const getIsFreeInvoice = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod === InvoicePaymentMethod.NOPAYMENT;
});

export const getPaymentMethod = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod && !getIsFreeInvoice(paymentMethod)
    ? getHumanReadableNameFromPaymentMethod(paymentMethod)
    : 'n/a';
});

export const getInvoiceCardNumber = memoize(
  (invoice) => {
    return isPaidByCreditCard(invoice.paymentMethod) && invoice.creditCardMaskedNumber ? (
      <span className="credit-card-number">{invoice.creditCardMaskedNumber}</span>
    ) : (
      <span className="not-applicable">n/a</span>
    );
  },
  (...args) => {
    return JSON.stringify(args);
  }
);

const getColumns = memoize((isSidebarOpen: boolean): SearchResultColumn[] => {
  const columns = [
    {
      name: 'Invoice Id',
      customClassNames: isSidebarOpen ? 'col-30' : 'col-20',
    },
    {
      name: 'Billing domain',
      customClassNames: 'col-20',
    },
    {
      name: 'Billing contact',
      customClassNames: 'col-10',
    },
    {
      name: !isSidebarOpen ? 'Payment method' : undefined,
      customClassNames: !isSidebarOpen ? 'col-10' : undefined,
    },
    {
      name: 'Card number',
      customClassNames: 'col-20',
    },
    {
      name: 'PO number',
      customClassNames: 'col-10',
    },
    {
      name: 'Date due/paid',
      customClassNames: 'col-10',
    },
    {
      name: 'Date created',
      customClassNames: 'col-10',
    },
    {
      name: 'Amount',
      customClassNames: 'col-10',
    },
    {
      name: '',
    },
  ];

  return columns;
});

export const defaultSearchOptions = {
  first: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

const InvoiceTabTxA = ({ id, label }: Props) => {
  const { isSidebarOpen, payload } = useSidebar();
  const { fetchMore, loading, error, data } = useQuery<
    FetchInvoicesForInvoiceGroupPageQuery,
    FetchInvoicesForInvoiceGroupPageQueryVariables
  >(fetchInvoicesForInvoiceGroupPage, {
    variables: {
      id: id,
      searchOptions: defaultSearchOptions,
    },
    displayName: 'FetchInvoicesForInvoiceGroupPageQuery',
    notifyOnNetworkStatusChange: true,
  });
  // variables to use for paging and populating our template
  const totalCount = data?.invoiceGroup?.invoices?.totalCount ?? 0;
  const hasNoResults = !loading && totalCount === 0;
  const nodes = data?.invoiceGroup?.invoices?.nodes;
  const resultsCount = nodes?.length ?? 0;

  const handleOnLoadMoreClick = () => {
    //@ts-ignore
    const cursor = data.invoiceGroup.invoices.pageInfo.endCursor;
    fetchMore({
      query: fetchInvoicesForInvoiceGroupPage,
      variables: {
        id: id,
        searchOptions: { ...defaultSearchOptions, cursor: cursor },
      }, //@ts-ignore
      updateQuery: (
        previousResult,
        { fetchMoreResult }: { fetchMoreResult: FetchInvoicesForInvoiceGroupPageQuery }
      ) => {
        if (fetchMoreResult?.invoiceGroup?.invoices?.nodes && previousResult?.invoiceGroup?.invoices?.nodes) {
          fetchMoreResult.invoiceGroup.invoices.nodes = [
            ...previousResult.invoiceGroup.invoices.nodes,
            ...fetchMoreResult.invoiceGroup.invoices?.nodes,
          ];
        }
        return fetchMoreResult;
      },
    });
  };
  return (
    <div className="invoicegroup-tab" data-testid="invoicegroup-invoice-tab">
      <SearchResultsTable
        id="invoices"
        isLoading={loading}
        error={error?.toString()}
        heading={label}
        noResults={hasNoResults}
        loadMore={{
          showLoadMore: totalCount !== 0 && resultsCount < totalCount,
          totalCount: totalCount,
          resultsCount: resultsCount,
          onLoadMoreClick: handleOnLoadMoreClick,
        }}
        entityName="invoices"
      >
        <ColGroup columns={getColumns(isSidebarOpen)} />
        <TableHead columns={getColumns(isSidebarOpen)} />
        <TableBody>
          {' '}
          {/*// @ts-ignore */}
          {(nodes ?? []).map((invoice: any, index: number) => {
            const {
              id,
              invoiceNumber,
              billingContactName,
              customerDomain,
              dueDate,
              grandTotal,
              paymentCurrency,
              purchaseOrderNumber,
              createdDate,
              paymentMethod,
              sourceSystem,
              refunds,
            } = invoice;

            const identifier = getIdentifier({
              id,
              invoiceNumber,
              sourceSystem,
            });
            const rowPayload = {
              invoiceId: identifier,
            };

            return (
              <>
                <OpenSidebarRow
                  key={`invoice-table-row-${index}`}
                  sidebar={GloballyAllowedSidebar.INVOICE_PREVIEW}
                  payload={rowPayload}
                  isSelected={false}
                  data-testid={`invoice-table-row-${index}`}
                >
                  <td title={getTextAndTitle(id)}>
                    <Link
                      to={`/invoices/${encodeURIComponent(identifier)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="avatar-container"
                      data-testid="invoice-link"
                    >
                      {/*// @ts-ignore */}
                      {<img src={InvoiceIcon} className="avatar medium not-round" />}
                      {identifier}
                    </Link>
                  </td>
                  <td>{customerDomain ?? <span className="not-applicable">n/a</span>}</td>
                  <td>{billingContactName ?? <span className="not-applicable">n/a</span>}</td>
                  <SimpleTD
                    text={getPaymentMethod(paymentMethod)}
                    className={classNames({
                      'not-applicable': getIsFreeInvoice(paymentMethod) || !paymentMethod,
                    })}
                  />
                  <td>{getInvoiceCardNumber(invoice)}</td>
                  <td>{purchaseOrderNumber ? purchaseOrderNumber : <span className="not-applicable">n/a</span>}</td>

                  <td>{dueDate ? <FormattedDate date={dueDate} /> : <span className="not-applicable">n/a</span>}</td>
                  <td>
                    {createdDate ? <FormattedDate date={createdDate} /> : <span className="not-applicable">n/a</span>}
                  </td>

                  <td className="invoice-amount-cell">
                    {!getIsFreeInvoice(paymentMethod) && grandTotal ? (
                      <FormattedCurrency value={grandTotal} currency={paymentCurrency} showCurrencyCode />
                    ) : (
                      'Free'
                    )}
                  </td>
                </OpenSidebarRow>

                {/* <FeatureSwitch flag={ENABLE_CREDIT_NOTES}>
                <Enabled> */}
                {!isEmpty(refunds) &&
                  //@ts-ignore
                  (refunds ?? []).map((ccpCreditNote: Refunds) => {
                    const refundPayload = {
                      refundId: ccpCreditNote?.creditNoteNumber || ccpCreditNote?.id,
                    };
                    const isChildRowSelected = isSidebarOpen && isEqual(refundPayload, payload);
                    return (
                      <RefundChildProductRow
                        key={ccpCreditNote.id}
                        parentColumns={9}
                        hideActions={false}
                        refund={ccpCreditNote}
                        isSelected={isChildRowSelected}
                      />
                    );
                  })}
                {/* </Enabled>
                </FeatureSwitch> */}
              </>
            );
          })}
        </TableBody>
      </SearchResultsTable>
    </div>
  );
};

export default InvoiceTabTxA;
